import merge from "lodash.merge";
import stateService, { isStateKey, type State, setStateKey } from "../state";
import { styles } from "../styles";
import {
  EMBEDDED_ID,
  EMBEDDED_IFRAME_CONTAINER_CLASS,
  EMBEDDED_IFRAME_PRELOAD_ID,
  EMBEDDED_OVERLAY_CLASS,
  EMBEDDED_OVERLAY_SELECTOR,
  EMBEDDED_OVERLAY_VISIBLE_CLASS,
  EMBEDDED_POPOVER_CLASS,
  EMBEDDED_POPOVER_CLOSE_CLASS,
} from "../utils/iframe";
import { closePopover } from "../utils/popover";

let listenerController: AbortController | undefined;

export const releaseListeners = () => {
  listenerController?.abort();
  listenerController = undefined;
};

export interface InitProps
  extends Pick<
      State,
      "screenConfiguration" | "theme" | "fontConfiguration" | "translation"
    >,
    Partial<
      Pick<State, "filters" | "prismaticUrl" | "skipPreload" | "agent">
    > {}

export const EMBEDDED_DEFAULTS = {
  filters: {
    marketplace: {
      includeActiveIntegrations: true,
    },
    integrations: {},
    components: {},
  },
  screenConfiguration: {
    configurationWizard: {},
    instance: {},
    marketplace: {},
    initializing: {},
  },
  skipPreload: false,
  theme: "LIGHT",
  fontConfiguration: undefined,
  translation: {},
};

/**
 * Initializes the Prismatic embedded SDK. This must be called once before any other
 * SDK methods. It sets up the popover overlay, preloads Prismatic assets into the
 * browser cache, and applies global configuration (theme, filters, translations, etc.).
 *
 * Calling `init` again resets the in-memory SDK state with the new options,
 * but the popover DOM and document-level listeners are reused. Call
 * {@link dispose} first if you need a fully fresh teardown.
 *
 * @param optionsBase - Optional global configuration for the embedded SDK.
 * @param optionsBase.theme - The color theme to use (`"LIGHT"` or `"DARK"`). Defaults to `"LIGHT"`.
 * @param optionsBase.fontConfiguration - Google Font families to load for the embedded UI.
 * @param optionsBase.screenConfiguration - Per-screen display options (marketplace, config wizard, dashboard, etc.).
 * @param optionsBase.filters - Default filters for the marketplace, integrations, and components screens.
 * @param optionsBase.translation - Custom phrase overrides and debug mode for i18n.
 * @param optionsBase.prismaticUrl - Override the Prismatic app URL. Defaults to `"https://app.prismatic.io"`.
 * @param optionsBase.skipPreload - Skip preloading Prismatic assets. Defaults to `false`.
 * @param optionsBase.agent - Configuration for the workflow copilot.
 * @param optionsBase.agent.context - Additional context for the copilot, keyed by topic. The agent consults these entries to learn your application's domain language, the components you expect it to use, or details about the signed-in user.
 *
 * @example
 * // Basic initialization with defaults
 * prismatic.init();
 *
 * @example
 * // Give the workflow copilot application-specific context
 * prismatic.init({
 *   agent: {
 *     context: {
 *       glossary: "A 'deal' is an opportunity record.",
 *       components: "Prefer the acmeLeads component for lead creation.",
 *     },
 *   },
 * });
 *
 * @example
 * // Initialize with dark theme and custom screen configuration
 * prismatic.init({
 *   theme: "DARK",
 *   screenConfiguration: {
 *     marketplace: { configuration: "allow-details" },
 *     configurationWizard: { mode: "streamlined" },
 *   },
 *   filters: {
 *     marketplace: { category: "ERP" },
 *   },
 * });
 *
 * @see {@link https://prismatic.io/docs/embed/get-started/install-embedded-sdk/ | Installing the Embedded SDK}
 * @see {@link https://prismatic.io/docs/embed/theming/ | Theming}
 * @see {@link https://prismatic.io/docs/embed/translations-and-internationalization/ | Translations & i18n}
 */
export const init = (optionsBase?: InitProps) => {
  const options: InitProps = merge({}, EMBEDDED_DEFAULTS, optionsBase);

  // when we initialize, start from the fresh default state
  const state = stateService.getInitialState();

  if (options) {
    Object.entries(options).forEach(([key, value]) => {
      if (isStateKey(key)) {
        setStateKey(state, key, value as State[typeof key]);
      }
    });
  }

  state.initComplete = true;

  stateService.setState(state);

  const existingElement = document.getElementById(EMBEDDED_ID);

  if (existingElement) {
    return;
  }

  /**
   * This establishes a connection to the prismatic url and preloads
   * assets (css, js, fonts, etc.) into browser cache. Subsequent
   * calls, use existing connections and cached assets.
   */
  const existingIframePreload = document.getElementById(
    EMBEDDED_IFRAME_PRELOAD_ID,
  );

  if (!existingIframePreload && !options.skipPreload) {
    document.body.insertAdjacentHTML(
      "beforeend",
      `<iframe
        id="${EMBEDDED_IFRAME_PRELOAD_ID}"
        src="${state.prismaticUrl}/embedded"
        style="visibility: hidden; display: none;"
        height="0"
        width="0"
      />`,
    );
  }

  document.head.insertAdjacentHTML("beforeend", styles);

  const embeddedElement = document.createElement("div");

  embeddedElement.id = EMBEDDED_ID;

  embeddedElement.innerHTML = /* html */ `
    <div class="${EMBEDDED_OVERLAY_CLASS}">
      <div class="${EMBEDDED_POPOVER_CLASS}">
        <button class="${EMBEDDED_POPOVER_CLOSE_CLASS}" aria-label="close popover" data-close>✕</button>
        <div class="${EMBEDDED_IFRAME_CONTAINER_CLASS}"></div>
      </div>
    </div>
  `;

  document.body.appendChild(embeddedElement);

  const closeButtonElement = document.querySelector(
    `#${EMBEDDED_ID} .${EMBEDDED_POPOVER_CLOSE_CLASS}`,
  );

  const overlayElement = document.querySelector(EMBEDDED_OVERLAY_SELECTOR);

  listenerController = new AbortController();
  const { signal } = listenerController;

  overlayElement?.addEventListener(
    "click",
    (event) => {
      if (event.target !== event.currentTarget) {
        return;
      }

      closePopover();
    },
    { signal },
  );

  closeButtonElement?.addEventListener("click", () => closePopover(), {
    signal,
  });

  document.addEventListener(
    "keyup",
    (e) => {
      if (
        e.key === "Escape" &&
        document.querySelector(`.${EMBEDDED_OVERLAY_VISIBLE_CLASS}`)
      ) {
        closePopover();
      }
    },
    { signal },
  );
};
